import cn from "classnames";
import React, { ReactElement } from "react";
import { BadgeStateType, BadgeType } from "../../types/badges";
import { SemanticTextLevels } from "../../types/colors";
import { TypographyVariantsTypes } from "../../types/typography";
import { Typography } from "../Typography";
import s from "./Badge.module.scss";

export type BadgeProps = {
  label: string;
  type?: BadgeType;
  state?: BadgeStateType;
  isUppercase?: boolean;
  isRounded?: boolean;
  rightIcon?: ReactElement;
};

/**
 * Badge
 *
 * @param label mandatory, label text to be displayed
 * @param type  optional, by default "primary", "primary" | "secondary" | "tertiary"
 * @param state optional, by default "general", Badge states "general" | "alert" | "success" | "failure" | "information" | "disabled"
 * @param isUppercase optional, boolean, by default false, to display label in uppercase
 * @param isRounded optional, boolean, by default false, to apply border radius
 * */
export function Badge({
  label,
  type = "primary",
  state = "general",
  isRounded = false,
  isUppercase = false,
  rightIcon,
}: BadgeProps) {
  const { color, variant } = React.useMemo((): {
    color: SemanticTextLevels;
    variant: TypographyVariantsTypes;
  } => {
    const variants: Record<string, TypographyVariantsTypes> = {
      primary: "tiny_extended_semi_bold",
      secondary: "tiny_extended_semi_bold",
      tertiary: "tiny_extended_semi_bold",
    };

    const stateStyles: Record<
      string,
      Record<
        string,
        {
          color: SemanticTextLevels;
          variant: TypographyVariantsTypes;
        }
      >
    > = {
      general: {
        primary: { color: "on_dark_bg", variant: variants.primary },
        secondary: { color: "secondary", variant: variants.secondary },
        tertiary: { color: "secondary", variant: variants.tertiary },
      },
      positive: {
        primary: { color: "on_dark_bg", variant: variants.primary },
        secondary: { color: "positive_dark", variant: variants.secondary },
        tertiary: { color: "positive_dark", variant: variants.tertiary },
      },
      negative: {
        primary: { color: "on_dark_bg", variant: variants.primary },
        secondary: { color: "negative", variant: variants.secondary },
        tertiary: { color: "negative", variant: variants.tertiary },
      },
      default: {
        primary: { color: "on_dark_bg", variant: variants.primary },
        secondary: { color: "brand", variant: variants.secondary },
        tertiary: { color: "brand", variant: variants.tertiary },
      },
      pending: {
        primary: { color: "on_dark_bg", variant: variants.primary },
        secondary: { color: "alert", variant: variants.secondary },
        tertiary: { color: "alert", variant: variants.tertiary },
      },
      disabled: {
        primary: { color: "disabled_alpha", variant: variants.primary },
        secondary: { color: "disabled_alpha", variant: variants.secondary },
        tertiary: { color: "disabled_alpha", variant: variants.tertiary },
      },
      information:{
        primary: { color: "on_dark_bg", variant: variants.primary },
        secondary: { color: "information", variant: variants.secondary },
        tertiary: { color: "information", variant: variants.tertiary },
      },
      warning: {
        primary: { color: "on_dark_bg", variant: variants.primary },
        secondary: { color: "warning", variant: variants.secondary },
        tertiary: { color: "warning", variant: variants.tertiary },
      },
    };

    return stateStyles[state][type];
  }, [type, state]);

  return (
    <div
      className={cn(s.badge, s[type], s[`${type}_${state}`], {
        [s.rounded]: isRounded,
      })}
    >
      <Typography
        as="span"
        label={isUppercase ? label?.toUpperCase() : label}
        variant={variant}
        color={color}
      />
      {!!rightIcon && (
        <div className={s.badgeIcon}>
          {React.cloneElement(rightIcon, {
            width: 12,
            height: 12,
            color,
          })}
        </div>
      )}
    </div>
  );
}
